import axios from 'axios';
import { startOfToday, subDays } from 'date-fns';
import { getText } from '@storebrand-digital/fms-translations';

import type Document from '../Document';
import type Filter from '../../Filter';
import { FetchDocumentsResult } from './types';
import generateCorrelationId from '../../../utils';
import { Segment, SegmentId } from '../../../utils/segments';

export function getDocumentsEndpoint(segmentId: SegmentId): string {
  switch (segmentId) {
    case Segment.BANK:
      return '/customer/document/archive/bank/documents';
    case Segment.MAIN:
    default:
      return '/customer/document/archive/documents/filter';
  }
}

function getDocumentName(
  documentIdentityCode: string | undefined,
  documentName: string
): string {
  if (
    documentIdentityCode === 'FOR2321' &&
    documentName === 'Forsikringsoversikt'
  ) {
    return getText('documentName.FOR2321');
  }
  return documentName;
}

export async function fetchDocumentsByFilters(
  filters: Filter,
  segmentId: SegmentId = Segment.MAIN
): Promise<FetchDocumentsResult> {
  if (
    segmentId === Segment.BANK &&
    !filters.createdFrom &&
    !filters.createdTo
  ) {
    return Promise.resolve({
      documents: [],
      pagination: {
        currentPage: 1,
        totalRecords: 0,
        totalPages: 1,
      },
    });
  }
  const endpoint = getDocumentsEndpoint(segmentId);
  const { segment, ...apiParams } = filters;
  return axios
    .post<Document.FilterAPI>(endpoint, apiParams, {
      headers: {
        ...{
          'X-Correlation-ID': `document-archive-web.${generateCorrelationId()}`,
        },
      },
    })
    .then(({ data }) => {
      const documents =
        data?.payload?.documents?.map(
          (entry) =>
            ({
              id: entry.id,
              name: getDocumentName(
                entry.documentIdentityCode,
                entry.documentName
              ),
              documentIdentityCode: entry.documentIdentityCode,
              date: entry.filedDate || entry.fileCreationDate,
              contractNumbers: entry.contractNumbers ?? [],
              products:
                entry.productDetails?.map(({ fmsKey }) =>
                  fmsKey.startsWith('sharedProductNames.')
                    ? getText(fmsKey)
                    : fmsKey
                ) ?? [],
              isRead: entry.readStatus,
              sourceType: entry.sourceType,
            }) satisfies Document
        ) ?? [];

      const currentPage = filters.page;
      const totalRecords = data?.payload?.total ?? 0;
      const totalPages = data?.payload?.totalPages ?? 1;

      return {
        documents: documents,
        pagination: {
          currentPage,
          totalRecords,
          totalPages,
        },
      };
    });
}

async function fetchDocumentUnreadCountTotal(): Promise<number> {
  return axios
    .get<Document.CountAPI>('/customer/document/archive/documents/count')
    .then(({ data }) => data?.payload?.total ?? 0);
}

async function fetchUnreadDocumentCountRecent(): Promise<number> {
  const twoWeeksAgo = subDays(startOfToday(), 14);

  const params = {
    createdFrom: twoWeeksAgo.toISOString(),
  };

  return axios
    .get<Document.CountAPI>('/customer/document/archive/documents/count', {
      params,
    })
    .then(({ data }) => data?.payload?.total ?? 0);
}

export async function fetchDocumentUnreadCount(): Promise<{
  recent: number;
  total: number;
}> {
  const [recent, total] = await Promise.all([
    fetchUnreadDocumentCountRecent(),
    fetchDocumentUnreadCountTotal(),
  ]);

  return { recent, total };
}
